import React, { useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiLock, FiKey, FiEye, FiEyeOff, FiCheckCircle, FiArrowLeft, FiUser } from 'react-icons/fi';

const ForgetPassword = ({ onBack }) => {
  const [step, setStep] = useState(1); // 1: email, 2: pin + nouveau mot de passe
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Vérifier l'email
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/check-email', { email });
      if (response.data.exists) {
        setUserInfo(response.data.user);
        setStep(2);
        toast.success('Email vérifié ! Entrez le code PIN admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Email non trouvé');
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le mot de passe avec PIN admin
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Validation du PIN
    if (!pin || pin.length !== 6) {
      toast.error('Code PIN à 6 chiffres requis');
      return;
    }
    
    if (pin !== '020260') {
      toast.error('Code PIN admin incorrect');
      return;
    }
    
    // Validation du mot de passe
    if (!newPassword || newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', {
        email,
        newPassword,
        confirmPassword,
        pin
      });
      
      if (response.data.success) {
        toast.success('Mot de passe réinitialisé avec succès !');
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        {/* Bouton retour */}
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 transition-colors"
        >
          <FiArrowLeft size={18} />
          Retour
        </button>

        {/* Logo */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <img 
              src="/logo.png" 
              alt="MST Logo" 
              className="w-16 h-16 rounded-full object-cover shadow-lg border-2 border-green-600"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden w-16 h-16 bg-green-600 rounded-full items-center justify-center">
              <span className="text-white font-bold text-xl">MST</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Mot de passe oublié</h2>
          <p className="text-gray-500 text-sm mt-1">
            {step === 1 ? 'Entrez votre email pour réinitialiser' : 'Entrez le code PIN admin et votre nouveau mot de passe'}
          </p>
        </div>

        {/* Étape 1: Email */}
        {step === 1 && (
          <form onSubmit={handleCheckEmail}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10"
                  placeholder="votre@email.com"
                  required
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Entrez l'email de votre compte MST
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              {loading ? 'Vérification...' : 'Vérifier l\'email'}
            </button>
          </form>
        )}

        {/* Étape 2: PIN Admin + Nouveau mot de passe */}
        {step === 2 && userInfo && (
          <form onSubmit={handleResetPassword}>
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">Compte trouvé :</p>
              <p className="font-semibold text-gray-800">{userInfo.name}</p>
              <p className="text-sm text-gray-500">{userInfo.email}</p>
            </div>
            
            {/* Code PIN Admin */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code PIN Admin
              </label>
              <div className="relative">
                <FiKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="input-field pl-10"
                  placeholder="Code PIN à 6 chiffres"
                  maxLength={6}
                  required
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Code PIN admin par défaut: contacter le super ADMIN pour l'obtenir
              </p>
            </div>

            <div className="border-t my-4"></div>

            {/* Nouveau mot de passe */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirmation du mot de passe */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            <div className="text-sm text-gray-500 mb-4 flex items-center gap-1">
              <FiCheckCircle className="text-green-600" size={14} />
              Le mot de passe doit contenir au moins 6 caractères
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgetPassword;